import React, { useContext } from 'react';
import {
  AmplitudeEvents,
  sendAmplitudeAnalyticEvent,
  sendRudderStackAnalyticEvent,
} from '@utils/analytics';
import ivclass from '@invideoio/web-shared/Modules/IVClass';
import ImageComponent from '@components/common/ImageComponent/ImageComponent';
import VideoComponent from '@components/common/VideoComponent/VideoComponent';
import { useEffect, useRef } from 'react';
import {
  CTATemplateUrl,
  CTAVideoWallTemplateUrl,
} from '../../constants/makepage';
import {
  ClickCtaUxSource,
  ClickCtaType,
  AnalyticsProductType,
} from '../../types/analytics.types';
import { StateContext } from '@store/store';
import { BaseAppType } from '@constants/common';

interface VideoTemplateProps {
  id: number;
  thumbnailURL: string;
  previewURL: string;
  play: boolean;
  width: number;
  height: number;
}

const VideoTemplate = (props: VideoTemplateProps) => {
  const { baseAppType } = useContext(StateContext);
  const { thumbnailURL, previewURL, play, width, height, id } = props;

  const videoElement = useRef<HTMLVideoElement>(null);
  const videoElementPromise = useRef<Promise<void> | null>(null);

  useEffect(() => {
    if (play) {
      playVideo();
    } else {
      pauseVideo();
    }
  }, [play]);

  const playVideo = () => {
    const video = videoElement?.current;
    if (video) {
      videoElementPromise.current = video.play();
    }
  };

  const pauseVideo = () => {
    const video = videoElement?.current;
    if (video && videoElementPromise.current) {
      videoElementPromise.current.then(() => {
        video.pause();
      });
      video.currentTime = 0;
    }
  };

  const openTemplate = () => {
    let clickEvent = AmplitudeEvents.openTemplate;

    sendAmplitudeAnalyticEvent(clickEvent, {
      'Template id': id,
    });
    sendRudderStackAnalyticEvent({
      event_name: 'click_cta',
      event_properties: {
        ux_source: ClickCtaUxSource.TemplateGrid,
        feature_utm: `template_id: ${id}`,
        type: ClickCtaType.ClickOpenTemplate,
        product:
          baseAppType === BaseAppType.AI
            ? AnalyticsProductType.AI
            : AnalyticsProductType.STUDIO,
      },
    });
    setTimeout(() => {
      let url = `${CTATemplateUrl}/${id}`;
      window.open(url, '_self');
    }, 20);
  };

  return (
    (thumbnailURL && (
      <div
        className={ivclass('iv-w-full iv-h-full', 'iv-cursor-pointer')}
        onClick={openTemplate}
      >
        <VideoComponent
          playsInline
          preload="none"
          loop
          muted
          data-poster={thumbnailURL}
          className={ivclass(
            'iv-object-cover',
            'iv-h-full iv-w-full',
            'lazyload',
          )}
          ref={videoElement}
        >
          <source data-src={previewURL} type="video/mp4" />
        </VideoComponent>
        <div className="iv-template-poster-image iv-absolute iv-top-0 iv-bottom-0 iv-h-full iv-w-full">
          <ImageComponent
            src={thumbnailURL}
            style={{
              width: '100%',
              height: 'auto',
            }}
            sizes="100vw"
            alt="logo"
            width={width}
            height={height}
            loading="lazy"
            elementype="next"
          />
        </div>
      </div>
    )) || <React.Fragment></React.Fragment>
  );
};

export default VideoTemplate;
